import cx from 'classnames';
import { forwardRef } from 'react';

export interface SliderProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * Colour of the filled part left of the thumb. `Metal` (the default) is the
   * long-standing grey; `Ink` is `label-primary`, which is what the prototype
   * passes as its slider `fillColor` — near-black on a light surface, and it
   * flips with the theme on its own. The track behind it is unchanged either way.
   */
  fill?: 'Metal' | 'Ink';
  /**
   * An explicit colour for the filled part, overriding `fill`.
   *
   * Any CSS colour. The fill is drawn as a very wide box-shadow on the thumb, so
   * this sets `--tw-shadow-color` inline — a custom property, which inherits
   * into the `::-webkit-slider-thumb` pseudo-element where the shadow is
   * declared, and beats the `fill` utility class on specificity.
   *
   * For a slider whose colour carries meaning rather than style: Inspect gives
   * each defect class its own accent, and the slider tuning that class is filled
   * in it so the control, the legend dot and the boxes on the camera frame all
   * read as one thing. The two-value `fill` enum cannot express that, and a
   * Tailwind class cannot either — the palette has to hand out the same value to
   * the canvas painter, which cannot resolve Tailwind classes at all.
   */
  fillColor?: string;
}

export const Slider = forwardRef<HTMLInputElement, SliderProps>(
  ({ className, fill = 'Metal', fillColor, style, ...rest }, ref) => {
    return (
      <input
        type="range"
        className={cx(
          className,
          'tw-block',
          'tw-bg-transparent',
          'tw-appearance-none',
          'tw-rounded-full',
          'focus-visible:tw-outline',
          '[&::-webkit-slider-runnable-track]:tw-bg-fill-secondary',
          '[&::-webkit-slider-runnable-track]:tw-rounded-full',
          '[&::-webkit-slider-runnable-track]:tw-overflow-hidden',
          '[&::-webkit-slider-runnable-track]:tw-h-[32px]',
          '[&::-webkit-slider-runnable-track]:tw-p-[2.5px]',
          '[&::-webkit-slider-thumb]:tw-appearance-none',
          '[&::-webkit-slider-thumb]:tw-rounded-full',
          '[&::-webkit-slider-thumb]:tw-h-[27px]',
          '[&::-webkit-slider-thumb]:tw-w-[27px]',
          '[&::-webkit-slider-thumb]:tw-bg-white',
          // this adds a subtle shadow on the thumb, plus a 2.5px outline, plus the shaded area to the left of it
          '[&::-webkit-slider-thumb]:[box-shadow:theme(boxShadow.i1),0_0_0_2.5px_var(--tw-shadow-color),-1014px_0_0_1000px_var(--tw-shadow-color)]',
          // Skipped when `fillColor` is given: the inline custom property below
          // would win anyway, and leaving the class on makes the resolved
          // colour ambiguous to anyone reading the DOM.
          fillColor == null &&
            (fill === 'Ink'
              ? '[&::-webkit-slider-thumb]:tw-shadow-label-primary'
              : '[&::-webkit-slider-thumb]:tw-shadow-metal-50 [&::-webkit-slider-thumb]:dark:tw-shadow-metal-70'),
          'tw-cursor-pointer',
          'disabled:tw-cursor-not-allowed',
          'disabled:tw-opacity-40',
        )}
        style={
          fillColor == null
            ? style
            : // Cast because `--*` custom properties are outside
              // CSSProperties' index signature.
              ({
                ...style,
                '--tw-shadow-color': fillColor,
              } as typeof style)
        }
        {...rest}
        ref={ref}
      />
    );
  },
);
